/**
 * Admin API Service for LAMED
 *
 * API client for admin-only endpoints (user management, audit logs)
 * All endpoints require admin authentication
 */

import apiClient from './api';
import type {
  User,
  UserCreateRequest,
  UserUpdateRequest,
  AuditLog,
  WebSocketEvent,
} from '@/types/user';

// ============================================================
// User Management API
// ============================================================

/**
 * Get all users with optional filters
 * @param status Filter by user status (active, blocked, archived)
 * @param role Filter by user role (admin, user)
 * @returns List of users
 * @throws Error if not authorized (401/403)
 */
export const listUsers = async (
  status?: string,
  role?: string
): Promise<User[]> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (role) params.append('role', role);

  const response = await apiClient.get<User[]>('/admin/users', { params });
  return response.data;
};

/**
 * Create a new user
 * @param data User creation data
 * @returns The created user
 * @throws Error if username exists (400) or not authorized (401/403)
 */
export const createUser = async (data: UserCreateRequest): Promise<User> => {
  const response = await apiClient.post<User>('/admin/users', data);
  return response.data;
};

/**
 * Update an existing user
 * @param userId User ID to update
 * @param data Fields to update
 * @returns The updated user
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const updateUser = async (
  userId: number,
  data: UserUpdateRequest
): Promise<User> => {
  const response = await apiClient.put<User>(`/admin/users/${userId}`, data);
  return response.data;
};

/**
 * Block a user account
 * @param userId User ID to block
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const blockUser = async (userId: number): Promise<void> => {
  await apiClient.post(`/admin/users/${userId}/block`);
};

/**
 * Unblock a user account
 * @param userId User ID to unblock
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const unblockUser = async (userId: number): Promise<void> => {
  await apiClient.post(`/admin/users/${userId}/unblock`);
};

/**
 * Archive a user account (soft delete)
 * @param userId User ID to archive
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const archiveUser = async (userId: number): Promise<void> => {
  await apiClient.post(`/admin/users/${userId}/archive`);
};

/**
 * Permanently delete a user
 * @param userId User ID to delete
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const deleteUser = async (userId: number): Promise<void> => {
  await apiClient.delete(`/admin/users/${userId}`);
};

// ============================================================
// Audit Log API
// ============================================================

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface WebSocketEventsResponse {
  events: WebSocketEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditLogFilters {
  limit?: number;
  offset?: number;
  level?: string;
  username?: string;
  session_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface WebSocketEventFilters {
  limit?: number;
  offset?: number;
  session_id?: string;
  username?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * Get audit logs with comprehensive filtering and pagination
 * @param filters Filter and pagination parameters
 * @returns Audit logs with pagination metadata
 * @throws Error if not authorized (401/403)
 */
export const getAuditLogs = async (
  filters: AuditLogFilters = {}
): Promise<AuditLogsResponse> => {
  const params = new URLSearchParams();
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
  if (filters.level) params.append('level', filters.level);
  if (filters.username) params.append('username', filters.username);
  if (filters.session_id) params.append('session_id', filters.session_id);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const response = await apiClient.get<AuditLogsResponse>('/admin/audit/logs', { params });
  return response.data;
};

/**
 * Get WebSocket events with filtering and pagination
 * @param filters Filter and pagination parameters
 * @returns WebSocket events with pagination metadata
 * @throws Error if not authorized (401/403)
 */
export const getWebSocketEvents = async (
  filters: WebSocketEventFilters = {}
): Promise<WebSocketEventsResponse> => {
  const params = new URLSearchParams();
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
  if (filters.session_id) params.append('session_id', filters.session_id);
  if (filters.username) params.append('username', filters.username);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const response = await apiClient.get<WebSocketEventsResponse>('/admin/audit/websocket-events', { params });
  return response.data;
};

/**
 * Get list of unique usernames that have audit logs
 * @returns Array of usernames
 * @throws Error if not authorized (401/403)
 */
export const getAuditUsernames = async (): Promise<string[]> => {
  const response = await apiClient.get<{ usernames: string[] }>('/admin/audit/usernames');
  return response.data.usernames;
};

/**
 * Get list of unique session IDs, optionally filtered by username
 * @param username Optional username to filter sessions
 * @returns Array of session IDs
 * @throws Error if not authorized (401/403)
 */
export const getAuditSessions = async (username?: string): Promise<string[]> => {
  const params = new URLSearchParams();
  if (username) params.append('username', username);

  const response = await apiClient.get<{ sessions: string[] }>('/admin/audit/sessions', { params });
  return response.data.sessions;
};
